import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { AiComparisonService } from '../../../core/admin/ai-comparison.service';
import { AiModelSummaryRow } from '../../../core/admin/ai-comparison.types';
import { AiEvaluationsService } from '../../../core/admin/ai-evaluations.service';
import {
  AiEvaluationFilterOption,
  AiEvaluationGroup,
  AiEvaluationsSummary,
} from '../../../core/admin/ai-evaluations.types';
import { IconComponent } from '../../../shared/icon/icon';
import { UsdPipe } from '../../../shared/pipes/usd.pipe';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

/** Badge variant for an advisory recommendation chip. */
type RecVariant = 'positive' | 'negative' | 'warning' | 'neutral';

const REC_VARIANT: Record<string, RecVariant> = {
  APPROVE: 'positive',
  DECLINE: 'negative',
  MORE_INFO: 'warning',
};

// Short chip text. The backend's `recommendation_display` ("Recommend request
// more information") is 34 characters and was forcing a 277px column; it stays
// on the chip as its accessible label rather than being dropped (FLOW-221 —
// fold, never remove).
const REC_SHORT: Record<string, string> = {
  APPROVE: 'Approve',
  DECLINE: 'Decline',
  MORE_INFO: 'More info',
};

@Component({
  selector: 'app-ai-evaluations',
  imports: [
    RouterLink,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    IconComponent,
    UsdPipe,
    DashPipe,
  ],
  templateUrl: './ai-evaluations.html',
  styleUrl: './ai-evaluations.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiEvaluationsComponent implements OnInit {
  private readonly service = inject(AiEvaluationsService);
  private readonly comparisonService = inject(AiComparisonService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly groups = signal<AiEvaluationGroup[]>([]);
  protected readonly summary = signal<AiEvaluationsSummary | null>(null);
  protected readonly purposeOptions = signal<AiEvaluationFilterOption[]>([]);
  protected readonly recommendationOptions = signal<AiEvaluationFilterOption[]>([]);

  /** Each model's record per kind of call, live runs and comparisons together. */
  protected readonly modelRows = signal<AiModelSummaryRow[]>([]);

  protected readonly loading = signal(true);
  protected readonly error = signal(false);

  protected readonly page = signal(1);
  protected readonly pageSize = signal(25);
  // Groups, not calls — the backend paginates by group so one is never split
  // across a page. `totalCalls` is the row count behind those groups.
  protected readonly total = signal(0);
  protected readonly totalCalls = signal(0);
  protected readonly hasNext = signal(false);

  protected readonly purposeFilter = signal<string>('');
  protected readonly recommendationFilter = signal<string>('');

  // 1-based group range shown in the footer, e.g. "1–25 of 142 subjects".
  protected readonly rangeStart = computed(() =>
    this.total() === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1,
  );
  protected readonly rangeEnd = computed(() =>
    Math.min(this.page() * this.pageSize(), this.total()),
  );

  /**
   * Whether the results span more than one page.
   *
   * Style guide: *"Hide the paginator entirely when results fit one page."*
   * A pager over a single row is two dead buttons and a count the table already
   * shows. ⚠️ Not `total() > 0` — that is true of one row.
   *
   * Read off `hasNext` rather than `ceil(total / pageSize)` so it stays correct
   * for an endpoint that reports `has_next` without a reliable total, and the
   * `page() > 1` limb keeps the pager present on the last page, which is the
   * only way back.
   */
  protected readonly showPager = computed(() => this.hasNext() || this.page() > 1);

  ngOnInit(): void {
    this.load();
    // Its own subscription: a failure here must not cost the page its log.
    this.comparisonService
      .modelSummary()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (summary) => this.modelRows.set(summary.rows) });
  }

  protected retry(): void {
    this.load();
  }

  /** Keyboard activation of a row (Enter); click is handled by routerLink. */
  protected openDetail(shortId: string): void {
    this.router.navigate(['/admin/ai-evaluations', shortId]);
  }

  protected recVariant(rec: string): RecVariant {
    return REC_VARIANT[rec] ?? 'neutral';
  }

  /** Short chip text; falls back to the backend label for an unmapped code. */
  protected recShort(rec: string, fallback: string): string {
    return REC_SHORT[rec] ?? fallback;
  }

  protected onPurposeChange(value: string): void {
    this.purposeFilter.set(value);
    this.page.set(1);
    this.load();
  }

  protected onRecommendationChange(value: string): void {
    this.recommendationFilter.set(value);
    this.page.set(1);
    this.load();
  }

  protected prevPage(): void {
    if (this.page() <= 1) return;
    this.page.update((p) => p - 1);
    this.load();
  }

  protected nextPage(): void {
    if (!this.hasNext()) return;
    this.page.update((p) => p + 1);
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.service
      .list({
        page: this.page(),
        page_size: this.pageSize(),
        purpose: this.purposeFilter() || undefined,
        recommendation: this.recommendationFilter() || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.groups.set(res.groups);
          this.summary.set(res.summary);
          this.purposeOptions.set(res.filters.purposes);
          this.recommendationOptions.set(res.filters.recommendations);
          this.total.set(res.total);
          this.totalCalls.set(res.total_calls);
          this.hasNext.set(res.has_next);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }
}
